import * as queryString from "querystring";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Pagination, quarks, ErrorState } from "ui-components";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { Button, Colors, Icon, Position, Tab, Tabs } from "@blueprintjs/core";
import { assertUnreachable } from "ui-lib";
import useSession from "ui-io/use-session";
import { tasksSearchQuery } from "ui-io/queries/tasks/tasks-query";
import {
  IN_PROGRESS_STATE,
  makeTaskT,
  TaskT,
  IN_COMPLETED_STATE,
  IN_PENDING_STATE,
  IN_INBOX_STATE,
  REJECTED_BY_PUBLISHER_TO_EDITOR,
  IN_APPROVED_STATE,
  IN_REJECTED_STATE,
} from "ui-io/task-io/task-t";
import {
  BulkEventType,
  FsmStateT,
  TaskAssignmentT,
  SortOrderT,
  taskPublishingActionKeys,
  taskAdminActionKeys,
} from "ui-io/task-io";
import { match } from "ts-pattern";
import { ActiveFilterT } from "ui-io/task-io/task-search";

import { NormalTaskColumn } from "../../components/task/task-columns";
// import RequestTaskButton from '../../components/task/tasks-table/request-task-button'
import {
  InboxViewIO,
  InboxViewT,
  TASK_INBOX_VIEWS,
  isBulkView,
  TaskInboxViewT,
  TaskInboxViewIO,
} from "./inbox-views";
// import ErrorState from '../../components/error-state'
import { goToInbox } from "./go-to-inbox";
import TasksTable from "../../components/task/tasks-table/tasks-table";
import TableLoading from "../../components/table-loading";
import { goToTask } from "../task/go-to-task";
import BulkActionsDropdown from "../../components/task/tasks-table/bulk-actions-dropdown";
import { TaskSortKey } from "../../components/task/task-sorters";
// import ReviewPage, { reviewJobViews } from '../../pages/review-page/index'
import useUserList from "../../components/use-user-list";
// import TaskSearchBox from '../../components/tasks-table/task-search-box'
import useTableFilters from "./use-table-filters";
import { formatLocation } from "../../routes";
import { PUBLISH_SEARCH_OPTIONS } from "./helper";
import { Tooltip2 } from "@blueprintjs/popover2";
import RefreshButton from "../../components/refresh-button";

export type TaskPageProps = {
  page?: number;
  view?: InboxViewT;
  state?: FsmStateT;
  assignee?: string;
  sortBy?: string;
  sortOrder?: SortOrderT;
  filter?: string;
};
export const standardColumns: NormalTaskColumn[] = [
  "taskType",
  "menuTask",
  "companyName",
  "taskActions",
  "configPriority",
  "taskAge",
  "lastModifiedTime",
  "taskLastModifiedBy",
];
const FMS_STATES_TO_TASK_ASSIGNMENTS: { [x: string]: TaskAssignmentT[] } = {
  [IN_PROGRESS_STATE]: ["CONFIG"],
};
const getFSMTaskAssignments = (
  view: InboxViewT,
): TaskAssignmentT[] | undefined => {
  switch (view) {
    case "Pending":
      return FMS_STATES_TO_TASK_ASSIGNMENTS[IN_PROGRESS_STATE];
    default:
      return undefined;
  }
};

const makeQueryBody: ({
  assignees,
  view,
}: {
  assignees?: string[];
  view: TaskInboxViewT;
}) => {
  states?: FsmStateT[];
  assignees?: string[];
} = ({ assignees, view }) => {
  switch (view) {
    case "Pending":
      return {
        states: [IN_PENDING_STATE as FsmStateT],
      };
    case "Approved":
      return {
        states: [IN_APPROVED_STATE as FsmStateT],
      };
    case "InReview":
      return {
        states: [IN_APPROVED_STATE as FsmStateT],
      };
    case "Rejected":
      return {
        states: [IN_REJECTED_STATE as FsmStateT],
      };
    case "Completed":
      return { states: [IN_COMPLETED_STATE as FsmStateT], assignees };
    case "Inbox":
      return {
        states: [
          IN_INBOX_STATE as FsmStateT,
          REJECTED_BY_PUBLISHER_TO_EDITOR as FsmStateT,
        ],
        assignees,
      };
    case "InProgress":
      return {
        states: [IN_PROGRESS_STATE as FsmStateT],
        assignees,
      };
    default:
      return assertUnreachable(view);
  }
};

export type TaskPanelProps = {
  page: number;
  view: TaskInboxViewT;
  assignee?: string;
  selectPage: (page: number) => void;
  sortBy: TaskSortKey;
  sortOrder: SortOrderT;
  activeFilterString?: string;
};
const TaskPanel = ({
  view,
  assignee,
  sortBy,
  sortOrder,
  activeFilterString,
  page,
}: TaskPanelProps) => {
  const history = useNavigate();
  const bulkActions: BulkEventType[] = match(view)
    .with( "InReview", () => taskPublishingActionKeys)
    .with("Pending", () => taskAdminActionKeys)
    .with("Inbox", "InProgress","Approved", "Completed", "Rejected", () => [])
    .exhaustive();
  const assignmentType = view;
  const [selectedTasks, setSelectedTasks] = useState<TaskT[]>([]);
  const onTasksSelect = useCallback(
    (tasks: TaskT[], selected: boolean) => {
      const withoutTarget = selectedTasks.filter(
        ({ id }) => !tasks.some((t) => t.id === id),
      );

      if (!selected) {
        setSelectedTasks(withoutTarget);
        return;
      }

      setSelectedTasks([...withoutTarget, ...tasks]);
    },
    [selectedTasks, setSelectedTasks],
  );

  const defaultColumns = match(view)
    .with("Pending", "Inbox", "InProgress", "Completed", () => standardColumns)
    .with("Approved", "InReview", "Rejected", (): NormalTaskColumn[] => [
      "taskType",
      "companyName",
      "taskActions",
      "configPriority",
      "taskAge",
    ])
    .exhaustive();
  const queryBody = makeQueryBody({
    assignees: typeof assignee === "string" ? [assignee] : undefined,
    view,
  });

  const allUserList = useUserList(isBulkView(view));
  const viewSearchOptions = PUBLISH_SEARCH_OPTIONS;
  const updateFilterInUrl = useCallback(
    (updatedFilter?: ActiveFilterT) => {
      const newFilter = updatedFilter
        ? JSON.stringify(updatedFilter)
        : undefined;
      const queryStringObj = queryString.parse(
        window.location.search.replace("?", ""),
      );
      if (queryStringObj.filter !== newFilter) {
        history(
          formatLocation.taskInbox({
            ...queryStringObj,
            // filter: newFilter,
            page: 0,
          }),
        );
        setSelectedTasks([]);
      }
    },
    [history],
  );
  const [appliedFilters, setFilters] = useTableFilters(
    viewSearchOptions,
    activeFilterString,
    updateFilterInUrl,
  );

  const pageSize = 20;
  const connection = useQuery(
    tasksSearchQuery({
      ...queryBody,
      ...appliedFilters,
      page: page || 0,
      pageSize: pageSize,
    }),
  );
  const { data: tasksResults } = connection;

  const tasks = useMemo(() => {
    const preparedTask =
      tasksResults?.hits.hits.map((hit: { _source: any }) =>
        makeTaskT(hit._source),
      ) ?? [];
    return preparedTask;
  }, [tasksResults]);

  // useEffect(() => {
  //   setSelectedTasks([]);
  // }, [tasks]);

  const setPage = (page: number) => {
    // goToInbox(history, {
    //   view,
    //   page: page,
    //   filter: activeFilterString ? activeFilterString : undefined,
    // })
  };

  const [recentlyAddedTaskId, setRecentlyAddedTaskId] = React.useState<
    string | undefined
  >(undefined);

  React.useEffect(() => {
    if (!recentlyAddedTaskId) return;

    const timeoutId = setTimeout(() => {
      setRecentlyAddedTaskId(undefined);
    }, 8000);

    return () => clearTimeout(timeoutId);
  }, [recentlyAddedTaskId]);

  switch (connection.status) {
    case "idle":
    case "loading":
      return <TableLoading />;
    case "error":
      return <ErrorState error={connection.error} />;
  }
  const totalNumberOfPages = tasksResults?.hits.total;
  // const assignments = getFSMTaskAssignments(view)
  const renderButtons = () => (
    <>
      {/* {assignments && assignmentType && (
        <RequestTaskButton
          assignmentType={assignmentType}
          assignments={assignments}
          setRecentlyAddedTaskId={setRecentlyAddedTaskId}
        />
      )} */}
      <Box>
        <Tooltip2
          content="Refresh"
          position={Position.LEFT}
          hoverOpenDelay={1000}
        >
          <RefreshButton
            className="mr-sm"
            handleSubmit={()=> connection.refetch()}
            isLoading={connection.isFetching as boolean}
          />
        </Tooltip2>
        {bulkActions.length > 0 && (
          <>
            {/* <TaskSearchBox
            setFilters={setFilters}
            appliedFilters={appliedFilters}
            viewSearchOptions={viewSearchOptions}
            showRightElement={false}
            isFavouritesEnabled={false}
          /> */}
            <BulkActionsDropdown
              actions={bulkActions}
              tasks={selectedTasks}
              allUserList={allUserList}
            />
          </>
        )}
      </Box>
    </>
  );
  return (
    <>
      <Box
        style={{
          opacity: connection?.isFetching ? 0.6 : 1,
          padding: 20,
        }}
      >
        <TasksTable
          recentlyRequestedTaskId={recentlyAddedTaskId}
          tasks={tasks}
          selectedTasks={selectedTasks}
          onTasksSelect={onTasksSelect}
          onClick={(task) => {
            localStorage.setItem("current-view", view);
            // const tabId = task._raw.companyDetail?.companyID
            //   ? "latestTestRun"
            //   : "scrapeConfig";

            goToTask(history, {
              taskId: task.id,
              ...(view === "Inbox"
                ? { tabId: view, subtabId: "company" }
                : { tabId: view }),
            });
          }}
          tableStoreId={`inbox-${view}`}
          defaultColumns={defaultColumns}
          defaultSort={sortBy}
          sortOrder={sortOrder}
          renderButtons={renderButtons}
          assignmentType={assignmentType}
          view={view}
          allUserList={allUserList}
          taskResults={tasksResults}
        />
      </Box>
      {totalNumberOfPages && totalNumberOfPages > pageSize ? (
        <Box
          style={{
            paddingBottom: 20,
          }}
          justify="center"
        >
          <Pagination
            count={Math.ceil(totalNumberOfPages / pageSize)}
            cursor={page || 0}
            onPaginate={setPage}
            maxCells={20}
          />
        </Box>
      ) : null}
    </>
  );
};

const TaskPage = (props: TaskPageProps) => {
  const { session } = useSession();
  const history = useNavigate();
  const taskPanelProps: Omit<TaskPanelProps, "selectPage"> = {
    view:
      (TaskInboxViewIO.is(props.view) && props.view) ||
      (TaskInboxViewIO.is(localStorage.getItem("selected-view")) &&
        (localStorage.getItem("selected-view") as TaskInboxViewT)) ||
      "Inbox",
    page: props.page ? Number(props.page) : 0,
    assignee:
      props.assignee ??
      // Note: For panels other than Admin (TODO), we generally expect the username to be the assignee assuming one isn't explicitly specified
      session?.username,
    sortBy: (props.sortBy as TaskSortKey) || "lastModifiedTime",
    sortOrder: props.sortOrder || "ASC",
    activeFilterString: props.filter || "",
  };
  const selectPage = (view: InboxViewT, page: number) => {
    goToInbox(history, { ...props, view, page: page });
  };
  const selectView = (view: InboxViewT) => {
    if (view === props.view) {
      return;
    }
    // goToInbox(history, {
    //   ...props,
    //   view,
    //   page: String(0),
    //   assignee: undefined,
    //   filter: undefined,
    //   workstreamIds: undefined,
    //   tags: undefined,
    //   taskTypes: undefined,
    //   text: undefined,
    //   priority: undefined,
    // })
  };
  // const inboxView = "Inbox"
  return (
    <TaskPanel
      {...taskPanelProps}
      selectPage={(page: number) =>
        selectPage(taskPanelProps.view as InboxViewT, page)
      }
      page={Number(props.page) || 0}
    />
  );
};

export default TaskPage;
